import type { SessionView, UserView } from '@hilo/contracts';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { PUBLIC_QUERY } from './admin';
import { api, ApiError, setCsrfToken } from './api';
import { clearAllDrafts } from './drafts';
import { keys } from './queries';

/** GET /auth/session: recupera el CSRF desde la cookie. `null` si no hay sesión. */
export async function fetchSession(signal?: AbortSignal): Promise<SessionView | null> {
  try {
    const session = await api<SessionView>('GET', '/auth/session', { signal });
    signal?.throwIfAborted();
    setCsrfToken(session.csrfToken);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
}

export function useSession() {
  return useQuery({ queryKey: keys.session, queryFn: () => fetchSession(), staleTime: Infinity, retry: 1 });
}

/** Solo un rechazo HTTP real invalida la sesión; red, timeout y 5xx se propagan al bucle de reconexión. */
export async function checkLiveSession(client: QueryClient, signal: AbortSignal): Promise<boolean> {
  try {
    if (await fetchSession(signal)) return true;
  } catch (error) {
    if (!(error instanceof ApiError) || (error.status !== 401 && error.status !== 403)) throw error;
  }
  forgetSession(client);
  return false;
}

/** El usuario de la sesión activa. Solo dentro de las rutas protegidas. */
export function useCurrentUser(): UserView {
  const client = useQueryClient();
  const session = client.getQueryData<SessionView | null>(keys.session);
  if (!session) throw new Error('useCurrentUser fuera de una sesión');
  return session.user;
}

/** Operador revisor de Meta: solo usa la bandeja. */
export function useIsMetaReviewer(): boolean {
  const client = useQueryClient();
  return client.getQueryData<SessionView | null>(keys.session)?.metaReviewer === true;
}

export async function login(client: QueryClient, email: string, password: string): Promise<SessionView> {
  const result = await api<{ user: UserView; csrfToken: string; metaReviewer?: boolean }>('POST', '/auth/login', { body: { email, password } });
  setCsrfToken(result.csrfToken);
  const session: SessionView = { ...result, mustChangePassword: result.user.mustChangePassword };
  client.setQueryData(keys.session, session);
  return session;
}

/** Olvida todo lo local: CSRF, borradores y caché (con contenido de clientes). Las consultas públicas se quedan. */
export function forgetSession(client: QueryClient): void {
  setCsrfToken(null);
  clearAllDrafts();
  client.removeQueries({ predicate: (query) => query.queryKey[0] !== keys.session[0] && query.queryKey[0] !== PUBLIC_QUERY });
  client.setQueryData(keys.session, null);
}

export async function logout(client: QueryClient): Promise<void> {
  try {
    await api<void>('POST', '/auth/logout');
  } finally {
    forgetSession(client);
  }
}
